import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, AlertCircle, CheckCircle2, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatPrice(v) {
  if (v == null) return "€0,00";
  return new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(v);
}
function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function ManualPaymentsTab({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/manual-payments`, {
        params: { page: 1, limit: 100 },
        headers,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Handmatige betalingen laden mislukt");
    }
    setLoading(false);
  }, [headers]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const retryEboek = async (payment_id) => {
    setRetrying(payment_id);
    try {
      const { data } = await axios.post(
        `${API}/admin/manual-payments/${payment_id}/eboekhouden-retry`,
        {},
        { headers }
      );
      if (data.success) {
        toast.success(`e-Boekhouden factuur ${data.invoice_number} aangemaakt`);
      } else {
        toast.error(`e-Boekhouden fout: ${data.error || "onbekend"}`);
      }
      await fetchItems();
    } catch (e) {
      toast.error(`Retry mislukt: ${e?.response?.data?.detail || e.message}`);
    }
    setRetrying(null);
  };

  return (
    <div data-testid="admin-manual-payments">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Smartphone size={22} className="text-[#FF3B30]" />
            Handmatige Mollie Betaallinks
          </h2>
          <p className="text-sm text-[#888] mt-1">
            Betaallinks aangemaakt via de Mollie-app of dashboard (buiten de webshop om). Automatisch geboekt in e-Boekhouden.nl.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchItems}
          disabled={loading}
          className="border-[#2a2a2a] text-white hover:bg-[#1a1a1a]"
          data-testid="refresh-manual-payments"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          <span className="ml-2">Vernieuwen</span>
        </Button>
      </div>

      <div className="text-xs text-[#666] mb-3">Totaal: <span className="text-white font-semibold">{total}</span> betalingen</div>

      <div className="bg-[#111] border border-[#1E1E1E] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[#888] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Factuur</th>
              <th className="text-left px-4 py-3">Datum</th>
              <th className="text-left px-4 py-3">Klant (iDEAL)</th>
              <th className="text-left px-4 py-3">Omschrijving</th>
              <th className="text-right px-4 py-3">Bedrag</th>
              <th className="text-center px-4 py-3">e-Boekhouden</th>
              <th className="text-right px-4 py-3">Actie</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-8 text-[#666]"><Loader2 size={18} className="animate-spin inline mr-2" />Laden...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-[#666]">Nog geen handmatige betalingen ontvangen.</td></tr>
            )}
            {!loading && items.map((it) => (
              <tr key={it.mollie_payment_id} className="border-t border-[#1E1E1E] hover:bg-[#0f0f0f]" data-testid={`manual-payment-${it.mollie_payment_id}`}>
                <td className="px-4 py-3 font-mono text-[#F1C40F]">{it.invoice_number || "-"}</td>
                <td className="px-4 py-3 text-[#ccc]">{formatDateTime(it.paid_at)}</td>
                <td className="px-4 py-3 text-white">
                  <div>{it.consumer_name || <span className="text-[#666]">-</span>}</div>
                  {it.consumer_account && <div className="text-[10px] text-[#666] font-mono mt-0.5">{it.consumer_account}</div>}
                </td>
                <td className="px-4 py-3 text-[#ccc] max-w-xs truncate">{it.description}</td>
                <td className="px-4 py-3 text-right">
                  <div className="text-white font-semibold">{formatPrice(it.amount_incl_vat)}</div>
                  <div className="text-[10px] text-[#666]">excl. {formatPrice(it.amount_ex_vat)} + BTW {formatPrice(it.vat_amount)}</div>
                </td>
                <td className="px-4 py-3 text-center">
                  {it.eboek_invoice_id ? (
                    <span className="inline-flex items-center gap-1 text-[#27AE60] text-xs" title={`ID: ${it.eboek_invoice_id}`}>
                      <CheckCircle2 size={14} /> Geboekt
                    </span>
                  ) : it.eboek_error ? (
                    <span className="inline-flex items-center gap-1 text-[#FF3B30] text-xs" title={it.eboek_error}>
                      <AlertCircle size={14} /> Fout
                    </span>
                  ) : (
                    <span className="text-[#666] text-xs">Niet geboekt</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={retrying === it.mollie_payment_id}
                    onClick={() => retryEboek(it.mollie_payment_id)}
                    className="border-[#2a2a2a] text-white hover:bg-[#1a1a1a] text-xs"
                    data-testid={`retry-${it.mollie_payment_id}`}
                  >
                    {retrying === it.mollie_payment_id ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    <span className="ml-1">{it.eboek_invoice_id ? "Opnieuw boeken" : "Retry"}</span>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
